import React, { useState } from 'react';
import { localDateString } from '../utils/dates';

const ACTIONS = [
  { id: 'done', label: 'Done', action: 'done' },
  { id: 'push', label: 'Already did it, push it out', action: 'push' },
  { id: 'skip-once', label: 'Skip this one', action: 'skip', skipScope: 'once' },
  { id: 'skip-year', label: 'Skip this year', action: 'skip', skipScope: 'year' },
];

function initialChoice(action, scope) {
  if (action === 'push') return 'push';
  if (action === 'skip') return scope === 'once' ? 'skip-once' : 'skip-year';
  return 'done';
}

/**
 * Email links land here. The token only pre-selects a button.
 * Nothing is saved until the homeowner taps Save.
 */
export default function TaskActionPage({ taskId, action, token, scope }) {
  const preset = initialChoice(action, scope);
  const [selected, setSelected] = useState(preset);
  const [doneDate, setDoneDate] = useState(localDateString(new Date()));
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    setSaving(true);
    setStatus('');
    try {
      const choice = ACTIONS.find((item) => item.id === selected) || ACTIONS[0];
      const response = await fetch('/.netlify/functions/task-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          action: choice.action,
          confirm: true,
          doneDate: choice.action === 'push' ? doneDate : undefined,
          skipScope: choice.skipScope,
          taskId,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setStatus(body.error || 'Could not update that task.');
        return;
      }
      setStatus(choice.id === 'skip-year'
        ? 'Skipped for this year.'
        : choice.id === 'skip-once'
          ? 'Skipped this one.'
          : choice.action === 'push'
            ? 'Marked done and the next one moved out.'
            : 'Marked done.');
    } catch (error) {
      setStatus('Could not update that task.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f4f7f7',
      padding: 'max(1.25rem, env(safe-area-inset-top)) 1rem max(1.5rem, env(safe-area-inset-bottom))',
      fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
    }}>
      <div style={{ maxWidth: '28rem', margin: '0 auto', background: '#fff', borderRadius: '0.75rem', padding: '1.25rem' }}>
        <h1 style={{ fontWeight: 500, fontSize: '1.4rem', marginTop: 0 }}>Still on your list</h1>
        <p style={{ color: '#667085' }}>
          Choose what to do, then tap Save. Opening this page does not change the task.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {ACTIONS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelected(item.id)}
              style={{
                minHeight: '52px',
                textAlign: 'left',
                padding: '0.75rem 1rem',
                borderRadius: '0.75rem',
                border: selected === item.id ? '2px solid #59c4c4' : '1px solid #d5e4e4',
                background: selected === item.id ? '#e7f7f7' : '#fff',
                fontSize: '1rem',
                fontWeight: 650,
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
        {selected === 'push' ? (
          <label style={{ display: 'block', marginTop: '1rem' }}>
            When did you do it?
            <input
              type="date"
              value={doneDate}
              onChange={(event) => setDoneDate(event.target.value)}
              style={{ display: 'block', width: '100%', minHeight: '48px', marginTop: '0.35rem', fontSize: '1rem' }}
            />
          </label>
        ) : null}
        <button
          type="button"
          onClick={confirm}
          disabled={saving || !token}
          style={{
            width: '100%',
            minHeight: '52px',
            marginTop: '1rem',
            border: 'none',
            borderRadius: '0.75rem',
            background: '#59c4c4',
            color: '#083838',
            fontWeight: 700,
            fontSize: '1rem',
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        {status ? <p role="status">{status}</p> : null}
      </div>
    </div>
  );
}
